import {createSetupIntent} from "@/lib/Stripe";
import {pushEvent} from "@/lib/gtmDataLayer";
import {
    getSessionStorageItem,
    removeSessionStorageItem,
    setSessionStorageItem,
} from "@/lib/storageUtils";

/** Matches braiv-firebase setup-session-urls constants. */
export const ON_DEMAND_SETUP_RESULT_PARAM = "onDemandSetup";
export const ON_DEMAND_SETUP_SESSION_PARAM = "session_id";

export const ON_DEMAND_SETUP_PENDING_KEY = "braiv_on_demand_setup_pending";
/** Set on successful setup return so Studio can prompt for shortfall credits. */
export const ON_DEMAND_SETUP_SUCCESS_PROMPT_KEY = "braiv_on_demand_setup_success_prompt";
const ON_DEMAND_SETUP_HANDLED_PREFIX = "braiv_on_demand_setup_handled:";

export function isInactiveCheckoutSessionError(message?: string | null): boolean {
    const normalized = (message || "").toLowerCase();
    return normalized.includes("no longer active") ||
        normalized.includes("session has expired") ||
        normalized.includes("session is expired");
}

export type OnDemandSetupReturnStatus = "success" | "cancelled";

export type OnDemandSetupReturnResult =
    | {status: "none"}
    | {status: OnDemandSetupReturnStatus; sessionId: string | null; isNew: boolean};

function readParam(
    searchParams: URLSearchParams | Record<string, string | string[] | undefined> | null | undefined,
    key: string,
): string | null {
    if (!searchParams) return null;
    if (searchParams instanceof URLSearchParams) {
        return searchParams.get(key);
    }
    const raw = searchParams[key];
    if (Array.isArray(raw)) return raw[0] ?? null;
    return typeof raw === "string" ? raw : null;
}

function handledKey(status: OnDemandSetupReturnStatus, sessionId: string | null): string {
    return `${ON_DEMAND_SETUP_HANDLED_PREFIX}${status}:${sessionId || "none"}`;
}

/**
 * Track On Demand activation start. Card entry is handled in-modal via AddCardModal.
 * Kept for analytics/pending-flag compatibility with the legacy redirect return handler.
 */
export function beginOnDemandSetup({
    returnPath,
    ctaLocation,
}: {
    returnPath: string;
    ctaLocation?: string;
}): void {
    setSessionStorageItem(ON_DEMAND_SETUP_PENDING_KEY, "1");
    pushEvent("begin_on_demand_setup", {
        cta_location: ctaLocation || "on_demand_setup",
        return_path: returnPath,
        mode: "elements",
    });
}

/**
 * Prefetch a Checkout setup-session client secret for in-modal card capture.
 */
export async function createOnDemandSetupIntent(): Promise<{
    clientSecret: string
    setupIntentId: string
    sessionId?: string
}> {
    return createSetupIntent();
}

/**
 * Parse setup-session return query params and dedupe via sessionStorage so
 * analytics / toasts fire once per browser session for a given result.
 * Kept for one release so in-flight redirect returns still resolve.
 */
export function consumeOnDemandSetupReturn(
    searchParams: URLSearchParams | Record<string, string | string[] | undefined> | null | undefined,
): OnDemandSetupReturnResult {
    const rawStatus = readParam(searchParams, ON_DEMAND_SETUP_RESULT_PARAM);
    if (rawStatus !== "success" && rawStatus !== "cancelled") {
        return {status: "none"};
    }

    const sessionId = rawStatus === "success"
        ? readParam(searchParams, ON_DEMAND_SETUP_SESSION_PARAM)
        : null;
    const key = handledKey(rawStatus, sessionId);
    const alreadyHandled = getSessionStorageItem(key) === "1";
    if (!alreadyHandled) {
        setSessionStorageItem(key, "1");
        removeSessionStorageItem(ON_DEMAND_SETUP_PENDING_KEY);
    }

    return {
        status: rawStatus,
        sessionId,
        isNew: !alreadyHandled,
    };
}

/** Build the current path without On Demand setup query markers. */
export function stripOnDemandSetupQuery(
    pathname: string,
    query: Record<string, string | string[] | undefined>,
): {pathname: string; query: Record<string, string | string[] | undefined>} {
    const nextQuery = {...query};
    delete nextQuery[ON_DEMAND_SETUP_RESULT_PARAM];
    delete nextQuery[ON_DEMAND_SETUP_SESSION_PARAM];
    return {pathname, query: nextQuery};
}
